import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Arrangement, TravelCompensationType } from '../types';

import {
  isUniqueArrangementCode,
  transformArrangementToCreateRequest
} from './innerView/utils/utils';

import { LoadingButton } from '@mui/lab';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { useSaveWithMutation } from '../hooks/useSaveArrangement';
import { createArrangementSchema } from '../schema';
import { BaseArrangementSection } from './BaseArrangementSection';
import DetailsArrangementSection from './DetailsArrangementSection';
import { useEffect } from 'react';

type Props = {
  rowData: Arrangement;
  rows: Arrangement[];
  handleClose: () => void;
  arrangementTypes: TravelCompensationType[];
};

export const ArrangementForm = ({ rowData, rows = [], handleClose }: Props) => {
  const { t } = useTranslation('common');

  const methods = useForm<any>({
    defaultValues: createArrangementSchema.cast(rowData),
    resolver: yupResolver(createArrangementSchema),
    mode: 'onChange'
  });

  const { mutate, isLoading } = useSaveWithMutation(handleClose);

  const { control, handleSubmit, watch, trigger } = methods;

  // watch for changes in the date fields
  const { from, to } = watch('travelSetLine');

  // trigger validation for 'to' when 'from' changes
  useEffect(() => {
    from && to && from.isBefore(to) && trigger('travelSetLine.to');
  }, [from]);
  // trigger validation for 'from' when 'to' changes
  useEffect(() => {
    from && to && to.isAfter(from) && trigger('travelSetLine.from');
  }, [to]);

  const onSubmit = (data) => {
    if (!isUniqueArrangementCode(data?.code, rowData?.code, rows)) {
      return methods.setError('code', {
        type: 'manual',
        message: t('validation.unique', {
          name: t('adminPortal.arrangements.code')
        })
      });
    }

    const dataToSave = transformArrangementToCreateRequest(data);
    mutate(dataToSave);
    handleClose();
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <BaseArrangementSection control={control} />
      <DetailsArrangementSection control={control} />
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
